/* The headline number on a dashboard.

   Lifted from the Grievance dashboard's local `Tile` so every module shows a
   KPI the same way. Two things are new here and deliberate: a tone for
   "warning" (the grievance screen only needed danger/success), and an optional
   `onClick` so a tile can drill through to the list it summarises — which is
   the first question anyone asks of a number they don't like. */
import { cn } from "../../utils/tailwindUtils";
import { Skeleton } from "../ui/skeleton";

/* Icon-chip colours. A map rather than a ternary chain so adding a tone is one
   line and the call sites stay readable. */
export const TILE_TONES = {
    default: "bg-sidebar-primary/10 text-sidebar-primary",
    success: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-300",
    danger: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300",
    warning: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300",
    info: "bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300",
};

const SURFACE_CLASS =
    "rounded-lg border border-gray-200 bg-white p-3.5 text-left dark:border-gray-700 dark:bg-gray-800/40";

export const StatTile = ({
    Icon,
    label,
    value,
    suffix,
    tone = "default",
    hint,
    onClick,
    loading = false,
}) => {
    // A dash, not a zero: "no data yet" and "the answer is zero" are different
    // facts and a dashboard that conflates them misleads.
    const hasValue = value !== null && value !== undefined;

    const body = (
        <>
            <div className="flex items-center gap-2">
                <span
                    className={cn(
                        "grid h-8 w-8 shrink-0 place-items-center rounded-lg",
                        TILE_TONES[tone] || TILE_TONES.default,
                    )}
                >
                    <Icon className="h-4 w-4" />
                </span>
                <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
                    {label}
                </p>
            </div>

            {loading ? (
                <Skeleton className="mt-2 h-8 w-20" />
            ) : (
                <p className="mt-2 text-2xl font-semibold tabular-nums text-gray-900 dark:text-white">
                    {hasValue ? value : "—"}
                    {hasValue && suffix && (
                        <span className="ml-1 text-sm font-normal text-muted-foreground">{suffix}</span>
                    )}
                </p>
            )}

            {hint && <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>}
        </>
    );

    if (!onClick) {
        return <div className={SURFACE_CLASS}>{body}</div>;
    }

    // A real <button>, so the tile is reachable by keyboard and announced as
    // actionable. A div with onClick looks identical and is unusable without a
    // mouse.
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                SURFACE_CLASS,
                "w-full transition-colors hover:border-sidebar-primary/40 hover:bg-gray-50",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-primary",
                "dark:hover:bg-gray-800/70",
            )}
        >
            {body}
        </button>
    );
};

/* The tile row. Two across on a phone, four on a wide screen — the grievance
   proportions. `columns` covers the dashboards whose KPIs group in threes. */
export const TileGrid = ({ columns = 4, className, children }) => (
    <div
        className={cn(
            "grid grid-cols-2 gap-3",
            columns === 3 ? "lg:grid-cols-3" : columns === 2 ? "lg:grid-cols-2" : "lg:grid-cols-4",
            className,
        )}
    >
        {children}
    </div>
);
